import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ChartConfiguration, Plugin } from 'chart.js';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AssetTypeClassification, ContainerRegisterReportDataDto, ContainerRegisterRowDto, ReportsService } from 'src/shared/service-proxies/service-proxies';
import { PageHelperService } from '../helpers/page-helper.service';
import { PagebaseComponent } from '../pagebase/pagebase.component';
import { ApplicationStatusService } from '../services/application-status.service';
import { CustomerSettings } from '../services/customer-settings-service';
import { Router } from '@angular/router';
import { SessionStorageService } from '../services/session-storage.service';
import { VehicleTypeHelper } from '../helpers/VehicleTypeHelper';

@Component({
  selector: 'app-container-register-report',
  templateUrl: './container-register-report.component.html',
  styleUrls: ['./container-register-report.component.scss'],
  standalone: false
})
export class ContainerRegisterReportComponent extends PagebaseComponent implements OnInit, OnDestroy {
  private static readonly STATE_KEY = 'container-register-report-state';
  @ViewChildren('containerSearchInput') private containerSearchInputs: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('locationSearchInput') private locationSearchInputs: QueryList<ElementRef<HTMLInputElement>>;

  customerId: string;
  data: ContainerRegisterReportDataDto;

  private apiCall: Subscription;
  private filterChange$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  loading: boolean = true;

  selectedContainerTypes: string[] = [];
  selectedLocationTypes: string[] = [];
  selectedDepotIds: string[] = [];
  selectedContainers: string[] = [];
  selectedLocations: string[] = [];
  selectedStatuses: string[] = [];
  containerSearchTerm = '';
  locationSearchTerm = '';

  containerOptions: string[] = [];
  locationOptions: string[] = [];
  statusOptions: string[] = [];

  totalAssets = 0;
  selectedVehicleType: string;
  selectedAssetType: string | null = null;
  selectedLocationType: string | null = null;
  selectedRow: ContainerRegisterRowDto | null = null;

  allRows: ContainerRegisterRowDto[] = [];
  filteredRows: ContainerRegisterRowDto[] = [];

  displayedColumns = ['containerId', 'assetType', 'locationName', 'colour', 'status', 'condition', 'info', 'rowAction'];
  sortState: { active: string; direction: 'asc' | 'desc' } | null = null;
  private restoredState = false;
  private selectedRowAssetId: string | null = null;

  private readonly piePalette: string[] = [
    '#4f86f7',
    '#e85c5c',
    '#34a853',
    '#f6bf26',
    '#8e24aa',
    '#fb8c00',
    '#6d4c41',
    '#d81b60',
    '#7cb342',
    '#ff7043',
    '#c0ca33',
    '#ad1457',
    '#43a047'
  ];

  barData: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: [{
      data: [],
      label: 'Count of Asset Type',
      backgroundColor: []
    }]
  };

  barOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 18
      }
    },
    plugins: {
      legend: { display: false }
    },
    scales: {
      x: {
        title: { display: true, text: 'Asset Type' }
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
        title: { display: true, text: 'Count of Asset Type' }
      }
    }
  };

  barPlugins: Plugin<'bar'>[] = [{
    id: 'barValueLabels',
    afterDatasetsDraw: (chart) => {
      const dataset = chart.data.datasets[0];
      if (!dataset?.data) {
        return;
      }

      const meta = chart.getDatasetMeta(0);
      const values = dataset.data as number[];
      const ctx = chart.ctx;
      ctx.save();
      ctx.font = '300 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = '#9ca3af';

      meta.data.forEach((bar, index) => {
        const value = values[index];
        if (value == null) {
          return;
        }

        const x = (bar as any).x as number;
        const y = (bar as any).y as number;
        ctx.fillText(String(value), x, y - 4);
      });

      ctx.restore();
    }
  }];

  pieData: ChartConfiguration<'pie'>['data'] = {
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: []
    }]
  };
  pieLegendItems: { label: string; color: string; value: number; percent: number }[] = [];

  pieOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    plugins: {
      legend: { display: false }
    }
  };

  piePlugins: Plugin<'pie'>[] = [{
    id: 'sliceLabels',
    afterDatasetsDraw: (chart) => {
      const ctx = chart.ctx;
      const dataset = chart.data.datasets[0];
      if (!dataset || !dataset.data) {
        return;
      }

      const values = dataset.data as number[];
      const total = values.reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0);
      if (!total) {
        return;
      }

      const meta = chart.getDatasetMeta(0);
      const fontSize = 11;
      const lineHeight = 12;
      ctx.save();
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#111';

      meta.data.forEach((arc, index) => {
        const value = values[index] ?? 0;
        if (!value) {
          return;
        }

        const startAngle = (arc as any).startAngle as number;
        const endAngle = (arc as any).endAngle as number;
        const innerRadius = (arc as any).innerRadius as number;
        const outerRadius = (arc as any).outerRadius as number;
        const angle = (startAngle + endAngle) / 2;
        const radius = (innerRadius + outerRadius) / 2;
        const x = (arc as any).x + Math.cos(angle) * radius;
        const y = (arc as any).y + Math.sin(angle) * radius;

        const percentValue = (value / total) * 100;
        if (percentValue <= 2) {
          return;
        }

        const percent = Math.round(percentValue);
        ctx.fillText(`${value}`, x, y - (lineHeight / 2));
        ctx.fillText(`(${percent}%)`, x, y + (lineHeight / 2));
      });

      ctx.restore();
    }
  }];

  constructor(
    private _reportsService: ReportsService,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    pageHelperService: PageHelperService,
    applicationStatusService: ApplicationStatusService,
    customerSettings: CustomerSettings
  ) {
    super(pageHelperService, applicationStatusService, customerSettings, 'Container Register');
  }

  ngOnInit(): void {
    this.restoreState();
    this.filterChange$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.getData());
  }

  ngOnDestroy(): void {
    this.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.filterChange$.complete();
  }

  unsubscribe(): void {
    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }
  }

  reset(): void {
    this.data = null;
    this.totalAssets = 0;
    this.selectedContainers = [];
    this.selectedLocations = [];
    this.selectedStatuses = [];
    this.containerSearchTerm = '';
    this.locationSearchTerm = '';
    this.containerOptions = [];
    this.locationOptions = [];
    this.statusOptions = [];
    this.selectedAssetType = null;
    this.selectedLocationType = null;
    this.selectedRow = null;
    this.selectedRowAssetId = null;
    this.allRows = [];
    this.filteredRows = [];
    this.barData = { labels: [], datasets: [{ data: [], label: 'Count of Asset Type', backgroundColor: [] }] };
    this.pieData = { labels: [], datasets: [{ data: [], backgroundColor: [] }] };
    this.pieLegendItems = [];

    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }
  }

  private resetFiltersForCustomerChange(): void {
    this.selectedContainerTypes = [];
    this.selectedLocationTypes = [];
    this.selectedDepotIds = [];
    this.selectedContainers = [];
    this.selectedLocations = [];
    this.selectedStatuses = [];
    this.containerSearchTerm = '';
    this.locationSearchTerm = '';
    this.containerOptions = [];
    this.locationOptions = [];
    this.statusOptions = [];
    this.selectedAssetType = null;
    this.selectedLocationType = null;
    this.selectedRow = null;
    this.selectedRowAssetId = null;
    this.clearState();
  }

  getData(): void {
    if (!this.restoredState) {
      this.restoreState();
    }

    if (this.customerId && this.customerId != this.pageHelperService.selectedCompanyId) {
      this.resetFiltersForCustomerChange();
    }
    this.customerId = this.pageHelperService.selectedCompanyId;

    if (!this.customerId || this.customerId == '00000000-0000-0000-0000-000000000000') {
      return;
    }

    const storedVehicleType = this.sessionStorageService.getItem('dashboard-active-selectedVehicleType');
    if (storedVehicleType) {
      this.selectedVehicleType = JSON.parse(storedVehicleType);
    } else {
      this.selectedVehicleType = null;
    }

    this.loading = true;
    this.setIsLoading(this.loading);

    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }

    this.apiCall = this._reportsService
      .getContainerRegister(
        this.customerId,
        this.getAssetTypeClassification(this.selectedVehicleType),
        [],
        null,
        this.selectedContainerTypes.length ? this.selectedContainerTypes : [],
        null,
        this.selectedLocationTypes.length ? this.selectedLocationTypes : [],
        null,
        this.selectedDepotIds.length ? this.selectedDepotIds : []
      )
      .subscribe(data => {
        this.data = data;
        this.allRows = [...(data?.rows ?? [])];
        this.buildLocalFilterOptions();
        this.reconcilePersistedSelections();
        this.refreshView();
        this.restoreSelectedRow();
        this.persistState();

        this.loading = false;
        this.setIsLoading(this.loading);
      }, error => {
        this.loading = false;
        this.setIsLoading(this.loading);
        console.error(error);
      });
  }

  onFilterChange(): void {
    this.persistState();
    this.filterChange$.next();
  }

  selectedVehicleTypeValueChange(newValue: string): void {
    if (newValue) {
      this.sessionStorageService.setItem('dashboard-active-selectedVehicleType', JSON.stringify(newValue));
    } else {
      this.sessionStorageService.removeItem('dashboard-active-selectedVehicleType');
    }

    this.selectedVehicleType = newValue;
    this.persistState();
    this.onFilterChange();
  }

  onLocalFilterChange(): void {
    this.refreshView();
    this.persistState();
  }

  get filteredContainerOptions(): string[] {
    return this.filterOptionsBySearch(this.containerOptions, this.containerSearchTerm, this.selectedContainers);
  }

  get filteredLocationOptions(): string[] {
    return this.filterOptionsBySearch(this.locationOptions, this.locationSearchTerm, this.selectedLocations);
  }

  onSelectOpenedChange(isOpen: boolean, filter: 'container' | 'location'): void {
    if (isOpen) {
      this.focusSearchInput(filter);
      return;
    }

    if (filter === 'container') {
      this.containerSearchTerm = '';
      return;
    }

    this.locationSearchTerm = '';
  }

  private focusSearchInput(filter: 'container' | 'location'): void {
    setTimeout(() => {
      const input = filter === 'container'
        ? this.containerSearchInputs?.first?.nativeElement
        : this.locationSearchInputs?.first?.nativeElement;

      input?.focus();
      input?.select();
    });
  }

  onSearchKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  onBarChartClick(event: any): void {
    const clickedIndex = event?.active?.[0]?.index;
    if (clickedIndex == null) {
      return;
    }

    const clickedType = this.barData.labels?.[clickedIndex] as string;
    this.selectedAssetType = this.selectedAssetType === clickedType ? null : clickedType;
    this.refreshView();
    this.persistState();
  }

  onPieChartClick(event: any): void {
    const clickedIndex = event?.active?.[0]?.index;
    if (clickedIndex == null) {
      return;
    }

    const clickedType = this.pieData.labels?.[clickedIndex] as string;
    this.selectedLocationType = this.selectedLocationType === clickedType ? null : clickedType;
    this.refreshView();
    this.persistState();
  }

  onRowClick(row: ContainerRegisterRowDto): void {
    this.selectedRow = this.selectedRow === row ? null : row;
    this.selectedRowAssetId = this.selectedRow ? this.getAssetId(this.selectedRow) : null;
    this.persistState();
  }

  onRowDoubleClick(row: ContainerRegisterRowDto): void {
    this.selectedRow = row;
    this.goToMoreInfo(row);
  }

  goToMoreInfo(row?: ContainerRegisterRowDto): void {
    const targetRow = row ?? this.selectedRow;
    if (!targetRow) {
      return;
    }

    const rowAsAny = targetRow as any;
    const assetId = rowAsAny?.assetId || targetRow.containerId;
    if (!assetId) {
      return;
    }

    this.selectedRow = targetRow;
    this.selectedRowAssetId = assetId;
    this.persistState();
    this.router.navigate(['/container-details', assetId]);
  }

  exportContainerRegister(): void {
    this.loading = true;
    this.setIsLoading(this.loading);

    this.customerId = this.pageHelperService.selectedCompanyId;
    if (!this.customerId) {
      this.loading = false;
      this.setIsLoading(this.loading);
      return;
    }

    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }

      this.apiCall = this._reportsService
        .exportContainerRegister({
          customerId: this.customerId,
          group: this.getAssetTypeClassification(this.selectedVehicleType),
          selectedGroups: [],
          containerType: null,
        selectedContainerTypes: this.selectedContainerTypes.length ? this.selectedContainerTypes : [],
        locationType: null,
        selectedLocationTypes: this.selectedLocationTypes.length ? this.selectedLocationTypes : [],
        depotId: null,
        selectedDepotIds: this.selectedDepotIds.length ? this.selectedDepotIds : []
      })
      .subscribe(data => {
        this.loading = false;
        this.setIsLoading(this.loading);
        const filename = data.fileName;
        const blob: Blob = data.data;
        const a = document.createElement('a');
        a.download = filename;
        a.href = window.URL.createObjectURL(blob);
        a.click();
      }, error => {
        this.loading = false;
        this.setIsLoading(this.loading);
        console.error(error);
      });
  }

  toggleSort(column: string): void {
    if (!this.sortState || this.sortState.active !== column) {
      this.sortState = { active: column, direction: 'asc' };
    } else if (this.sortState.direction === 'asc') {
      this.sortState = { active: column, direction: 'desc' };
    } else {
      this.sortState = null;
    }

    this.applySort();
    this.persistState();
  }

  getSortDirection(column: string): 'asc' | 'desc' | null {
    if (!this.sortState || this.sortState.active !== column) {
      return null;
    }
    return this.sortState.direction;
  }

  private refreshView(): void {
    const baseRows = this.allRows.filter(r =>
      (!this.selectedContainers.length || this.selectedContainers.includes(this.getContainerValue(r))) &&
      (!this.selectedLocations.length || this.selectedLocations.includes(this.normalizeLabel(r.locationName))) &&
      (!this.selectedStatuses.length || this.selectedStatuses.includes(this.normalizeLabel(r.status)))
    );

    const rowsForBar = this.selectedLocationType
      ? baseRows.filter(r => this.normalizeLabel(r.locationType) === this.selectedLocationType)
      : baseRows;

    const rowsForPie = this.selectedAssetType
      ? baseRows.filter(r => this.normalizeLabel(r.assetType) === this.selectedAssetType)
      : baseRows;

    this.updateBarChart(rowsForBar);
    this.updatePieChart(rowsForPie);

    this.filteredRows = baseRows.filter(r =>
      (!this.selectedAssetType || this.normalizeLabel(r.assetType) === this.selectedAssetType) &&
      (!this.selectedLocationType || this.normalizeLabel(r.locationType) === this.selectedLocationType)
    );

    this.totalAssets = this.filteredRows.length;
    this.applySort();

    if (this.selectedRow && !this.filteredRows.includes(this.selectedRow)) {
      this.selectedRow = null;
      this.selectedRowAssetId = null;
    }
  }

  private updateBarChart(rows: ContainerRegisterRowDto[]): void {
    const counts = new Map<string, number>();
    rows.forEach(row => {
      const key = this.normalizeLabel(row.assetType);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });

    const sortedEntries = Array.from(counts.entries())
      .sort((a, b) => {
        if (b[1] !== a[1]) {
          return b[1] - a[1];
        }
        return a[0].localeCompare(b[0]);
      });

    const labels = sortedEntries.map(([label]) => label);
    const values = sortedEntries.map(([, value]) => value);
    const barColors = labels.map(label =>
      !this.selectedAssetType || this.selectedAssetType === label ? '#4f86f7' : 'rgba(79, 134, 247, 0.3)'
    );

    this.barData = {
      labels,
      datasets: [{
        data: values,
        label: 'Count of Asset Type',
        backgroundColor: barColors,
        borderColor: '#3d71d2',
        borderWidth: 1,
        borderRadius: 4,
        maxBarThickness: 56
      }]
    };
  }

  private updatePieChart(rows: ContainerRegisterRowDto[]): void {
    const counts = new Map<string, number>();
    rows.forEach(row => {
      const key = this.normalizeLabel(row.locationType);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });

    const labels = Array.from(counts.keys()).sort((a, b) => a.localeCompare(b));
    const values = labels.map(label => counts.get(label) ?? 0);

    const colors = labels.map((label, index) => {
      const base = this.piePalette[index % this.piePalette.length];
      if (!this.selectedLocationType || this.selectedLocationType === label) {
        return base;
      }
      return 'rgba(0, 0, 0, 0.18)';
    });

    const total = values.reduce((sum, value) => sum + value, 0);
    this.pieLegendItems = total
      ? labels
          .map((label, index) => ({
            label,
            value: values[index],
            color: this.piePalette[index % this.piePalette.length]
          }))
          .filter(item => item.value > 0)
          .sort((a, b) => (b.value !== a.value ? b.value - a.value : a.label.localeCompare(b.label)))
          .slice(0, 2)
          .map(item => ({
            ...item,
            percent: Math.round((item.value / total) * 100)
          }))
      : [];

    this.pieData = {
      labels,
      datasets: [{
        data: values,
        backgroundColor: colors
      }]
    };
  }

  private normalizeLabel(value: string | undefined | null): string {
    const trimmed = value?.trim();
    return trimmed ? trimmed : 'Unknown';
  }

  private getAssetTypeClassification(selectedVehicleType: string): AssetTypeClassification {
    return !selectedVehicleType ? AssetTypeClassification.All : VehicleTypeHelper.ConvertVehicleTypeNameToAssetTypeClassification(selectedVehicleType);
  }

  private getAssetId(row: ContainerRegisterRowDto | null | undefined): string | null {
    if (!row) {
      return null;
    }

    const rowAsAny = row as any;
    return (rowAsAny?.assetId as string) ?? row.containerId ?? null;
  }

  private getContainerValue(row: ContainerRegisterRowDto): string {
    return this.normalizeLabel(this.getAssetId(row));
  }

  private buildLocalFilterOptions(): void {
    const containers = new Set<string>();
    const locations = new Set<string>();
    const statuses = new Set<string>();

    this.allRows.forEach(row => {
      containers.add(this.getContainerValue(row));
      locations.add(this.normalizeLabel(row.locationName));
      statuses.add(this.normalizeLabel(row.status));
    });

    this.containerOptions = this.sortOptions(containers);
    this.locationOptions = this.sortOptions(locations);
    this.statusOptions = this.sortOptions(statuses);

    this.selectedContainers = this.selectedContainers.filter(x => containers.has(x));
    this.selectedLocations = this.selectedLocations.filter(x => locations.has(x));
    this.selectedStatuses = this.selectedStatuses.filter(x => statuses.has(x));
  }

  private reconcilePersistedSelections(): void {
    const assetTypes = new Set(this.allRows.map(row => this.normalizeLabel(row.assetType)));
    const locationTypes = new Set(this.allRows.map(row => this.normalizeLabel(row.locationType)));

    if (this.selectedAssetType && !assetTypes.has(this.selectedAssetType)) {
      this.selectedAssetType = null;
    }

    if (this.selectedLocationType && !locationTypes.has(this.selectedLocationType)) {
      this.selectedLocationType = null;
    }
  }

  private restoreSelectedRow(): void {
    if (!this.selectedRowAssetId) {
      this.selectedRow = null;
      return;
    }

    this.selectedRow = this.filteredRows.find(row => this.getAssetId(row) === this.selectedRowAssetId) ?? null;
    if (!this.selectedRow) {
      this.selectedRowAssetId = null;
    }
  }

  private restoreState(): void {
    const activeCustomerId = this.pageHelperService.selectedCompanyId;
    if (!activeCustomerId) {
      return;
    }

    this.restoredState = true;

    const rawState = this.sessionStorageService.getItem(ContainerRegisterReportComponent.STATE_KEY);
    if (!rawState) {
      return;
    }

    try {
      const state = JSON.parse(rawState);
      if (!state || state.customerId !== activeCustomerId) {
        return;
      }

      this.selectedVehicleType = state.selectedVehicleType ?? null;
      this.selectedContainerTypes = Array.isArray(state.selectedContainerTypes) ? state.selectedContainerTypes : [];
      this.selectedLocationTypes = Array.isArray(state.selectedLocationTypes) ? state.selectedLocationTypes : [];
      this.selectedDepotIds = Array.isArray(state.selectedDepotIds) ? state.selectedDepotIds : [];
      this.selectedContainers = Array.isArray(state.selectedContainers) ? state.selectedContainers : [];
      this.selectedLocations = Array.isArray(state.selectedLocations) ? state.selectedLocations : [];
      this.selectedStatuses = Array.isArray(state.selectedStatuses) ? state.selectedStatuses : [];
      this.selectedAssetType = state.selectedAssetType ?? null;
      this.selectedLocationType = state.selectedLocationType ?? null;
      this.selectedRowAssetId = state.selectedRowAssetId ?? null;
      this.sortState = state.sortState ?? null;
    } catch {
      this.clearState();
    }
  }

  private persistState(): void {
    const customerId = this.pageHelperService.selectedCompanyId ?? this.customerId;
    if (!customerId || customerId === '00000000-0000-0000-0000-000000000000') {
      return;
    }

    this.sessionStorageService.setItem(
      ContainerRegisterReportComponent.STATE_KEY,
      JSON.stringify({
        customerId,
        selectedVehicleType: this.selectedVehicleType ?? null,
        selectedContainerTypes: this.selectedContainerTypes,
        selectedLocationTypes: this.selectedLocationTypes,
        selectedDepotIds: this.selectedDepotIds,
        selectedContainers: this.selectedContainers,
        selectedLocations: this.selectedLocations,
        selectedStatuses: this.selectedStatuses,
        selectedAssetType: this.selectedAssetType ?? null,
        selectedLocationType: this.selectedLocationType ?? null,
        selectedRowAssetId: this.selectedRowAssetId,
        sortState: this.sortState
      })
    );
  }

  private clearState(): void {
    this.sessionStorageService.removeItem(ContainerRegisterReportComponent.STATE_KEY);
  }

  private sortOptions(values: Set<string>): string[] {
    return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  private filterOptionsBySearch(options: string[], searchTerm: string, selectedValues: string[]): string[] {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return options;
    }

    return options.filter(option =>
      selectedValues.includes(option) || option.toLowerCase().includes(term)
    );
  }

  private applySort(): void {
    if (!this.filteredRows?.length || !this.sortState) {
      return;
    }

    const { active, direction } = this.sortState;
    const multiplier = direction === 'asc' ? 1 : -1;

    this.filteredRows = [...this.filteredRows].sort((a, b) => {
      const left = this.getSortValue(a, active);
      const right = this.getSortValue(b, active);

      if (left == null && right == null) {
        return 0;
      }
      if (left == null) {
        return 1;
      }
      if (right == null) {
        return -1;
      }

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * multiplier;
      }

      const leftString = String(left).toLowerCase();
      const rightString = String(right).toLowerCase();
      if (leftString < rightString) {
        return -1 * multiplier;
      }
      if (leftString > rightString) {
        return 1 * multiplier;
      }
      return 0;
    });
  }

  private getSortValue(row: ContainerRegisterRowDto, column: string): string | number | null {
    switch (column) {
      case 'containerId':
        return row.containerId ?? null;
      case 'assetType':
        return row.assetType ?? null;
      case 'locationName':
        return row.locationName ?? null;
      case 'colour':
        return row.colour ?? null;
      case 'status':
        return row.status ?? null;
      case 'condition':
        return row.condition ?? null;
      case 'info':
        return row.info ?? null;
      default:
        return null;
    }
  }
}
